/**
 * disputesSlice — Redux Toolkit state + thunks for the admin disputes
 * lifecycle (CON-PAYMENTS-004).
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   // List page
 *   disputes: Dispute[];
 *   pagination: DisputePaginationMeta | null;
 *   isLoadingList: boolean;
 *   listError: string | null;
 *
 *   // Detail page
 *   currentDispute: Dispute | null;
 *   evidenceFiles: DisputeEvidenceFile[];
 *   isLoadingDetail: boolean;
 *   detailError: string | null;
 *
 *   // Evidence submission
 *   isSubmitting: boolean;
 *   submitError: string | null;
 * }
 * ```
 *
 * Thunks
 * ------
 * - `fetchDisputes(params)` — paginated list with optional filters/sort.
 * - `fetchDispute(disputeId)` — single dispute + its evidence files.
 * - `submitDisputeEvidence(orderId, disputeId, payload)` — POST evidence.
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only the view-level containers do.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  getAdminDispute,
  getDisputeEvidenceFiles,
  listAdminDisputes,
  submitDisputeEvidence as apiSubmitEvidence,
} from '@dfq/shared/api/admin/disputes';
import { ApiError } from '@dfq/shared/api/_envelope';
import type {
  Dispute,
  DisputeEvidenceFile,
  DisputeListParams,
  DisputePaginationMeta,
  SubmitDisputeEvidencePayload,
} from '@dfq/shared/types/payments';

// ----------------------------------------------------------------------------
// State
// ----------------------------------------------------------------------------

export interface DisputesState {
  /** Current page of disputes for the list view. */
  disputes: Dispute[];
  /** Pagination meta from the last successful list fetch. */
  pagination: DisputePaginationMeta | null;
  isLoadingList: boolean;
  listError: string | null;

  /** The dispute currently open in the detail view. */
  currentDispute: Dispute | null;
  /** Evidence files attached to the current dispute. */
  evidenceFiles: DisputeEvidenceFile[];
  isLoadingDetail: boolean;
  detailError: string | null;

  /** Evidence submission lifecycle. */
  isSubmitting: boolean;
  submitError: string | null;
}

const initialState: DisputesState = {
  disputes: [],
  pagination: null,
  isLoadingList: false,
  listError: null,

  currentDispute: null,
  evidenceFiles: [],
  isLoadingDetail: false,
  detailError: null,

  isSubmitting: false,
  submitError: null,
};

// ----------------------------------------------------------------------------
// Thunk error type
// ----------------------------------------------------------------------------

type ThunkApiError = { rejectValue: ApiError };

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * Fetch a paginated list of disputes. The container controls pagination,
 * sort, and filter state; the thunk just calls the API and stores the result.
 */
export const fetchDisputes = createAsyncThunk<
  { disputes: Dispute[]; pagination: DisputePaginationMeta },
  DisputeListParams | undefined,
  ThunkApiError
>('disputes/fetchList', async (params = {}, { rejectWithValue }) => {
  try {
    return await listAdminDisputes(params);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error);
    }
    return rejectWithValue(
      new ApiError({ message: 'Unexpected error fetching disputes', status: 0 }),
    );
  }
});

/**
 * Fetch a single dispute by id AND its associated evidence files.
 * Dispatched when the detail view mounts.
 */
export const fetchDispute = createAsyncThunk<
  { dispute: Dispute; evidenceFiles: DisputeEvidenceFile[] },
  number,
  ThunkApiError
>('disputes/fetchDetail', async (disputeId, { rejectWithValue }) => {
  try {
    const [dispute, evidenceFiles] = await Promise.all([
      getAdminDispute(disputeId),
      getDisputeEvidenceFiles(disputeId),
    ]);
    return { dispute, evidenceFiles };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error);
    }
    return rejectWithValue(
      new ApiError({ message: 'Unexpected error fetching dispute detail', status: 0 }),
    );
  }
});

/**
 * Submit evidence for a dispute. On success the detail is refetched so
 * the evidence list refreshes. Callers await the thunk and handle toasts.
 */
export const submitDisputeEvidence = createAsyncThunk<
  Dispute,
  { orderId: number; disputeId: number; payload: SubmitDisputeEvidencePayload },
  ThunkApiError
>('disputes/submitEvidence', async ({ orderId, disputeId, payload }, { rejectWithValue }) => {
  try {
    return await apiSubmitEvidence(orderId, disputeId, payload);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error);
    }
    return rejectWithValue(
      new ApiError({ message: 'Unexpected error submitting evidence', status: 0 }),
    );
  }
});

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

const disputesSlice = createSlice({
  name: 'disputes',
  initialState,
  reducers: {
    /** Reset detail state when leaving the detail view. */
    clearCurrentDispute(state) {
      state.currentDispute = null;
      state.evidenceFiles = [];
      state.detailError = null;
    },
    /** Reset evidence submission error (e.g. after the form is corrected). */
    clearSubmitError(state) {
      state.submitError = null;
    },
  },
  extraReducers: (builder) => {
    // fetchDisputes
    builder
      .addCase(fetchDisputes.pending, (state) => {
        state.isLoadingList = true;
        state.listError = null;
      })
      .addCase(fetchDisputes.fulfilled, (state, action) => {
        state.isLoadingList = false;
        state.disputes = action.payload.disputes;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchDisputes.rejected, (state, action: PayloadAction<ApiError | undefined>) => {
        state.isLoadingList = false;
        state.listError = action.payload?.message ?? 'Failed to load disputes';
      });

    // fetchDispute (detail)
    builder
      .addCase(fetchDispute.pending, (state) => {
        state.isLoadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchDispute.fulfilled, (state, action) => {
        state.isLoadingDetail = false;
        state.currentDispute = action.payload.dispute;
        state.evidenceFiles = action.payload.evidenceFiles;
      })
      .addCase(fetchDispute.rejected, (state, action: PayloadAction<ApiError | undefined>) => {
        state.isLoadingDetail = false;
        state.detailError = action.payload?.message ?? 'Failed to load dispute';
      });

    // submitDisputeEvidence
    builder
      .addCase(submitDisputeEvidence.pending, (state) => {
        state.isSubmitting = true;
        state.submitError = null;
      })
      .addCase(submitDisputeEvidence.fulfilled, (state, action) => {
        state.isSubmitting = false;
        // Patch the current dispute with the updated state from the server
        state.currentDispute = action.payload;
      })
      .addCase(
        submitDisputeEvidence.rejected,
        (state, action: PayloadAction<ApiError | undefined>) => {
          state.isSubmitting = false;
          state.submitError = action.payload?.message ?? 'Failed to submit evidence';
        },
      );
  },
});

export const { clearCurrentDispute, clearSubmitError } = disputesSlice.actions;
export const disputesReducer = disputesSlice.reducer;
